import { Injectable } from '@angular/core';
import { ClientLoggingService } from '../shared/client-logging.service';
import { ArchitecturalPlanPage } from '../shared/models/architectural-plan-page.model';
import { parseScaleFromText } from '../components/project/pages/preview/measure-overlay/measure-utils';

/** Result of scale detection for a page. */
export interface ScaleDetectionResult {
  /** Human-readable scale text (e.g., '1/4" = 1\'-0"'). Null if not detected. */
  scaleText: string | null;
  /** Numeric multiplier (e.g., 48 for 1/4" = 1'-0"). Null if not detected. */
  scaleFactor: number | null;
  /** Source of the detection. */
  source: 'transcript' | 'default';
}

/**
 * Service to detect the drawing scale from an architectural plan page transcript.
 *
 * Strategy:
 * 1. Parse `page.getPageMarkdown()` with regex/heuristic matching against
 *    common architectural scale patterns (e.g., "SCALE 1/4" = 1'-0"", "1:48").
 * 2. Cache results per page to avoid redundant parsing.
 *
 * Future enhancement: Replace with a Flash Lite LLM call or a backend
 * decorator that persists the scale during ingestion.
 *
 * Related: Issue #1212
 */
@Injectable({
  providedIn: 'root',
})
export class ScaleDetectionService {
  /** In-memory cache keyed by `${projectId}::${pageNumber}::${fileId}`. */
  private cache = new Map<string, ScaleDetectionResult>();

  /** Max cache entries before eviction (prevents unbounded memory growth). */
  private static readonly MAX_CACHE_SIZE = 500;

  /**
   * Detect the drawing scale from a page's transcript text.
   * Returns immediately with a cached or freshly parsed result.
   */
  detectScale(page: ArchitecturalPlanPage): ScaleDetectionResult {
    const cacheKey = this.getCacheKey(page);

    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const transcript = page.getPageMarkdown?.() || '';
    const parsed = parseScaleFromText(transcript);

    const result: ScaleDetectionResult = parsed
      ? { scaleText: parsed.scaleText, scaleFactor: parsed.scaleFactor, source: 'transcript' }
      : { scaleText: null, scaleFactor: null, source: 'default' };

    ClientLoggingService.log(
      'DEBUG',
      `[ScaleDetection] Page ${page.getPageNumber()} scale: ${result.scaleText ?? 'not detected'} (factor=${result.scaleFactor})`
    );

    // Evict oldest half when cache exceeds limit
    if (this.cache.size >= ScaleDetectionService.MAX_CACHE_SIZE) {
      const keysToDelete = [...this.cache.keys()].slice(0, Math.floor(this.cache.size / 2));
      for (const key of keysToDelete) {
        this.cache.delete(key);
      }
    }

    this.cache.set(cacheKey, result);
    return result;
  }

  /** Clear cache for a specific page. */
  invalidate(page: ArchitecturalPlanPage): void {
    this.cache.delete(this.getCacheKey(page));
  }

  /** Clear entire cache. */
  clearCache(): void {
    this.cache.clear();
  }

  private getCacheKey(page: ArchitecturalPlanPage): string {
    const projectId = page.getArchitecturalPlanId?.() || '';
    const pageNumber = page.getPageNumber?.() || 0;
    const fileId = page.getFileId?.() || '';
    return `${projectId}::${pageNumber}::${fileId}`;
  }
}
